import { useEffect, useRef, useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Loader2, Plus } from "lucide-react";
import { isValidSharedConversationUrl } from "~/components/assistant/constants";
import type { ConversationShortcut } from "~/components/assistant/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import useErrorToast from "~/contexts/error-toast";
import { apiMutate } from "~/utils/apiClient";
import ConversationShortcutRow from "./ConversationShortcutRow";

const { VITE_CATALOG_API } = import.meta.env;

type SaveState = "idle" | "saving" | "saved";

const MAX_SHORTCUTS = 6;
const DEBOUNCE_MS = 800;

const ConversationShortcutsTab = () => {
  const { data: dataset, mutate } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const serverShortcuts = dataset?.conversationShortcuts ?? [];
  const serverKey = JSON.stringify(serverShortcuts);

  const [shortcuts, setShortcuts] = useState<ConversationShortcut[]>(() => serverShortcuts);
  const [saveStates, setSaveStates] = useState<SaveState[]>(() =>
    serverShortcuts.map(() => "idle" as SaveState)
  );
  const [lastServerKey, setLastServerKey] = useState(serverKey);

  // Sync from server on initial load or external updates
  if (serverKey !== lastServerKey) {
    setShortcuts(serverShortcuts);
    setSaveStates(serverShortcuts.map(() => "idle" as SaveState));
    setLastServerKey(serverKey);
  }

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const save = async (toSave: ConversationShortcut[], changedIndices: number[]) => {
    if (!dataset) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSaveStates((prev) => {
      const next = [...prev];
      for (const i of changedIndices) {
        if (i < next.length) next[i] = "saving";
      }
      return next;
    });

    try {
      await apiMutate(
        `${VITE_CATALOG_API}/datasets/${dataset.id}/conversation-shortcuts`,
        "PUT",
        { shortcuts: toSave },
        { signal: controller.signal }
      );

      setLastServerKey(JSON.stringify(toSave));

      setSaveStates((prev) => {
        const next = [...prev];
        for (const i of changedIndices) {
          if (i < next.length) next[i] = "saved";
        }
        return next;
      });

      mutate((current) => {
        if (!current || current.id !== dataset.id) return current;
        return { ...current, conversationShortcuts: toSave };
      });
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      console.error(e);
      showError("Unable to save conversation shortcuts");
      setSaveStates((prev) => {
        const next = [...prev];
        for (const i of changedIndices) {
          if (i < next.length) next[i] = "idle";
        }
        return next;
      });
    }
  };

  const debounceSave = (newShortcuts: ConversationShortcut[]) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const changedIndices: number[] = [];
    for (let i = 0; i < newShortcuts.length; i++) {
      if (JSON.stringify(newShortcuts[i]) !== JSON.stringify(serverShortcuts[i])) {
        changedIndices.push(i);
      }
    }

    if (changedIndices.length === 0) return;

    const hasInvalidUrl = newShortcuts.some(
      (s) => s.url !== "" && !isValidSharedConversationUrl(s.url)
    );
    if (hasInvalidUrl) return;

    timerRef.current = setTimeout(() => {
      save(newShortcuts, changedIndices);
    }, DEBOUNCE_MS);
  };

  const handleChangeLabel = (index: number, label: string) => {
    const updated = [...shortcuts];
    updated[index] = { ...updated[index], label };
    setShortcuts(updated);
    setSaveStates((prev) => {
      const next = [...prev];
      next[index] = "idle";
      return next;
    });
    debounceSave(updated);
  };

  const handleChangeUrl = (index: number, url: string) => {
    const updated = [...shortcuts];
    updated[index] = { ...updated[index], url };
    setShortcuts(updated);
    setSaveStates((prev) => {
      const next = [...prev];
      next[index] = "idle";
      return next;
    });
    debounceSave(updated);
  };

  const handleAdd = () => {
    if (shortcuts.length >= MAX_SHORTCUTS) return;
    const updated = [...shortcuts, { url: "", label: "" }];
    setShortcuts(updated);
    setSaveStates((prev) => [...prev, "idle"]);
  };

  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (deleteIndex === null || !dataset) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    const updated = shortcuts.filter((_, i) => i !== deleteIndex);

    setDeleting(true);
    try {
      await apiMutate(`${VITE_CATALOG_API}/datasets/${dataset.id}/conversation-shortcuts`, "PUT", {
        shortcuts: updated,
      });

      setLastServerKey(JSON.stringify(updated));
      setShortcuts(updated);
      setSaveStates((prev) => prev.filter((_, i) => i !== deleteIndex));
      mutate((current) => {
        if (!current || current.id !== dataset.id) return current;
        return { ...current, conversationShortcuts: updated };
      });
      setDeleteIndex(null);
    } catch (e) {
      console.error(e);
      showError("Unable to delete conversation shortcut");
    }
    setDeleting(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Pin up to 6 featured conversation links that all users will see in the conversation history.
        URLs must point to shared conversations.
      </p>
      <div className="flex flex-col gap-4">
        {shortcuts.map((shortcut, index) => (
          <ConversationShortcutRow
            key={index}
            index={index}
            label={shortcut.label}
            url={shortcut.url}
            saveState={saveStates[index] ?? "idle"}
            urlInvalid={shortcut.url !== "" && !isValidSharedConversationUrl(shortcut.url)}
            disabled={!isOwner}
            onChangeLabel={(value) => handleChangeLabel(index, value)}
            onChangeUrl={(value) => handleChangeUrl(index, value)}
            onDelete={() => setDeleteIndex(index)}
          />
        ))}
      </div>
      {isOwner && shortcuts.length < MAX_SHORTCUTS && (
        <div>
          <Button variant="secondary" size="sm" onClick={handleAdd}>
            <Plus className="size-4 mr-1" />
            Add Shortcut
          </Button>
        </div>
      )}
      <AlertDialog
        open={deleteIndex !== null}
        onOpenChange={(open) => !deleting && !open && setDeleteIndex(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shortcut</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this conversation shortcut?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} onClick={() => setDeleteIndex(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="size-4 mr-1 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConversationShortcutsTab;
